import React, { useEffect, useState } from "react";
import Logo from "../../assets/images/logo-primary.png";
import LogoWhite from "../../assets/images/logo-secondary.png";
import LogoUltimate from "../../assets/images/logo-bjb-ultimate.png";
import LogoUltimateWhite from "../../assets/images/logo-bjb-ultimate-white.png";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import Navlink from "./Navlink";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { setLocale } from "../../redux/Slice";
import { useLocation, useNavigate } from "react-router-dom";
import { isAuthenticated, removeToken } from "../../lib/api";

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navbarData = t("navbar", { returnObjects: true });
  const [navbar, setNavbar] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [isAuth, setIsAuth] = useState(false);

  useEffect(() => {
    setIsAuth(isAuthenticated());
  }, [pathname]);

  const handleLogout = () => {
    removeToken();
    setIsAuth(false);
    navigate("/login");
  };

  const onChangeLocale = (lang) => {
    i18n.changeLanguage(lang);
    dispatch(setLocale(lang));
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setActiveDropdown(null);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleChildClick = () => {
    setNavbar(false);
    setActiveDropdown(null);
  };

  return (
    <div
      role="navigation"
      className={`w-full fixed z-30 top-0 transition-all duration-500 ease-in-out px-6 py-4 md:px-12 navbar-main ${isScrolled || navbar
        ? "bg-black/40 backdrop-blur-lg text-white"
        : "bg-white text-brand-blue"
        }`}
    >
      <div className="container mx-auto">

        {/* ════════ DESKTOP LAYOUT (lg+): 2-column layout with overflowing left logo ════════ */}
        {/* Column 1 — Main Logo (Absolute positioned to overlap/clip the navbar) */}
        <a
          href="/"
          className="hidden lg:block absolute top-4 left-6 xl:left-12 z-40 transition-transform duration-300 hover:scale-105"
        >
          {isScrolled ? (
            <img src={LogoWhite} alt="Logo" className="w-32 xl:w-40" />
          ) : (
            <img src={Logo} alt="Logo" className="w-32 xl:w-40" />
          )}
        </a>

        {/* Column 2 — Right content: partner logo (row 1) and nav menu (row 2) */}
        <div className="hidden lg:flex flex-col items-end justify-between gap-2 w-full">

          {/* Row 1 — Ultimate partner logo, right-aligned */}
          <div className="flex justify-end">
            <a
              href="/"
              className="transition-transform duration-300 hover:scale-105"
            >
              {isScrolled ? (
                <img
                  src={LogoUltimateWhite}
                  alt="Ultimate 10K Series Logo"
                  className="w-32 xl:w-40"
                />
              ) : (
                <img
                  src={LogoUltimate}
                  alt="Ultimate 10K Series Logo"
                  className="w-32 xl:w-40"
                />
              )}
            </a>
          </div>

          {/* Row 2 — Navigation menu, right-aligned */}
          <ul
            className={`flex flex-row items-center justify-end gap-6 xl:gap-8 font-evil antialiased text-base xl:text-lg 2xl:text-xl ${isScrolled ? "text-white" : "text-brand-blue"
              }`}
          >
            {navbarData.map((value, index) => {
              if (value.isHide) return null;
              const hasChild =
                Array.isArray(value.child) && value.child.length > 0;

              return (
                <li
                  key={index}
                  className="relative inline-block"
                  onMouseEnter={() =>
                    hasChild && setActiveDropdown(value.label)
                  }
                  onMouseLeave={() =>
                    hasChild && setActiveDropdown(null)
                  }
                >
                  <Navlink
                    label={value.label}
                    href={value.href}
                    isButton={value.isButton || false}
                    isScrolled={isScrolled}
                    navbar={false}
                  />

                  {hasChild && (
                    <div
                      className={`absolute right-0 top-full mt-3 z-50 transition-all duration-200 ${activeDropdown === value.label
                        ? "opacity-100 visible"
                        : "opacity-0 invisible"
                        }`}
                      style={{ minWidth: "220px" }}
                    >
                      <ul className="bg-[#1b4580] text-white py-2 rounded-md shadow-xl border border-white/10 divide-y divide-white/10">
                        {value.child.map((child, idx) =>
                          child.isHide ? null : (
                            <li
                              key={idx}
                              className="hover:bg-black/20 transition-colors duration-200"
                            >
                              <a
                                href={child.href}
                                target={child.target || (child.href?.startsWith("http") || child.href?.endsWith(".pdf") ? "_blank" : undefined)}
                                rel={child.target === "_blank" || child.href?.startsWith("http") || child.href?.endsWith(".pdf") ? "noopener noreferrer" : undefined}
                                className="block py-3 px-6 text-center text-sm xl:text-base font-evil tracking-wider hover:text-brand-orange transition-colors duration-200"
                              >
                                {child.label}
                              </a>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  )}
                </li>
              );
            })}

            {isAuth ? (
              <>
                <li>
                  <Navlink
                    label="Profile"
                    href="/profile"
                    isScrolled={isScrolled}
                    navbar={false}
                  />
                </li>
                <li>
                  <button
                    onClick={handleLogout}
                    className="font-evil hover:text-brand-orange transition-colors duration-200"
                  >
                    Logout
                  </button>
                </li>
              </>
            ) : (
              <li>
                <Navlink
                  label="REGISTER NOW"
                  href="/login"
                  isScrolled={isScrolled}
                  navbar={false}
                />
              </li>
            )}

            {/* Language Switcher */}
            <li className="flex items-center gap-3 ml-2">
              <button
                onClick={() => onChangeLocale("en")}
                className={`transition-colors duration-200 hover:text-brand-orange ${i18n.language === "en" ? "text-brand-orange font-bold" : ""
                  }`}
              >
                EN
              </button>
              <span className="opacity-40">|</span>
              <button
                onClick={() => onChangeLocale("id")}
                className={`transition-colors duration-200 hover:text-brand-orange ${i18n.language === "id" ? "text-brand-orange font-bold" : ""
                  }`}
              >
                ID
              </button>
            </li>
          </ul>
        </div>

        {/* ════════ MOBILE LAYOUT (< lg) ════════ */}
        <div className="lg:hidden">

          {/* Top bar: main logo | partner logo + hamburger */}
          <div className="flex items-center justify-end w-full relative min-h-[48px]">
            <a
              href="/"
              className="absolute top-4 left-6 z-40 transition-transform duration-300 hover:scale-105"
            >
              {navbar || isScrolled ? (
                <img src={LogoWhite} alt="Logo" className="w-24 md:w-32" />
              ) : (
                <img src={Logo} alt="Logo" className="w-24 md:w-32" />
              )}
            </a>

            <div className="flex items-center gap-3">
              <a
                href="/"
                className="transition-transform duration-300 hover:scale-105"
              >
                {navbar || isScrolled ? (
                  <img
                    src={LogoUltimateWhite}
                    alt="Ultimate 10K Series Logo"
                    className="w-24 md:w-32"
                  />
                ) : (
                  <img
                    src={LogoUltimate}
                    alt="Ultimate 10K Series Logo"
                    className="w-24 md:w-32"
                  />
                )}
              </a>
              <button
                className="p-2 rounded-md outline-none"
                onClick={() => setNavbar(!navbar)}
              >
                {navbar ? (
                  <XMarkIcon width={24} className="text-white" />
                ) : (
                  <Bars3Icon
                    className={isScrolled ? "text-white" : "text-[#164481]"}
                    width={24}
                  />
                )}
              </button>
            </div>
          </div>

          {/* Mobile expanded menu */}
          <div
            className={`transition-all duration-300 ease-in-out ${navbar ? "block" : "hidden"
              }`}
          >
            <ul className="flex flex-col items-center gap-4 font-evil antialiased text-base text-white py-6">
              {navbarData.map((value, index) => {
                if (value.isHide) return null;
                const hasChild =
                  Array.isArray(value.child) && value.child.length > 0;

                return (
                  <li key={index} className="relative w-full text-center">
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        if (hasChild) {
                          setActiveDropdown((prev) =>
                            prev === value.label ? null : value.label
                          );
                        } else {
                          setActiveDropdown(null);
                        }
                      }}
                    >
                      <Navlink
                        label={value.label}
                        href={value.href}
                        isButton={value.isButton || false}
                        isScrolled={isScrolled}
                        navbar={navbar}
                      />
                    </div>

                    {hasChild && (
                      <div
                        className={`mt-2 ${activeDropdown === value.label ? "block" : "hidden"
                          }`}
                      >
                        <ul className="space-y-2 py-2">
                          {value.child.map((child, idx) =>
                            child.isHide ? null : (
                              <li
                                key={idx}
                                className="hover:text-brand-orange text-center"
                              >
                                <a
                                  href={child.href}
                                  target={child.target || (child.href?.startsWith("http") || child.href?.endsWith(".pdf") ? "_blank" : undefined)}
                                  rel={child.target === "_blank" || child.href?.startsWith("http") || child.href?.endsWith(".pdf") ? "noopener noreferrer" : undefined}
                                  className="block py-2 text-sm"
                                  onClick={handleChildClick}
                                >
                                  {child.label}
                                </a>
                              </li>
                            )
                          )}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}

              {isAuth ? (
                <>
                  <li className="relative w-full text-center">
                    <Navlink
                      label="Profile"
                      href="/profile"
                      isScrolled={isScrolled}
                      navbar={navbar}
                    />
                  </li>
                  <li className="relative w-full text-center">
                    <button
                      onClick={() => {
                        handleLogout();
                        setNavbar(false);
                      }}
                      className="font-evil text-white hover:text-brand-orange transition-colors duration-200"
                    >
                      Logout
                    </button>
                  </li>
                </>
              ) : (
                <li className="relative w-full text-center">
                  <Navlink
                    label="Login"
                    href="/login"
                    isScrolled={isScrolled}
                    navbar={navbar}
                  />
                </li>
              )}

              {/* Mobile Language Switcher */}
              <li className="flex items-center gap-4 mt-2">
                <button
                  onClick={() => onChangeLocale("en")}
                  className={`transition-colors duration-200 hover:text-brand-orange ${i18n.language === "en" ? "text-brand-orange font-bold" : ""
                    }`}
                >
                  EN
                </button>
                <span className="opacity-40">|</span>
                <button
                  onClick={() => onChangeLocale("id")}
                  className={`transition-colors duration-200 hover:text-brand-orange ${i18n.language === "id" ? "text-brand-orange font-bold" : ""
                    }`}
                >
                  ID
                </button>
              </li>
            </ul>
          </div>
        </div>

      </div>
    </div>
  );
}
